import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalCounterpartFields from 'chemistry/molecules/ChemicalCounterpartFields';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {withLoadingScreen} from 'util/Promises';

export interface EditChemicalStructureModalProps {
  moleculeId: string;
  expectedCurrentVersionId: string;
  initialChemicalStructure: ChemicalStructureDrawerValue;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function EditChemicalStructureModal({
  moleculeId,
  expectedCurrentVersionId,
  initialChemicalStructure,
  onRequestClose,
  onRequestSubmit,
}: EditChemicalStructureModalProps): JSX.Element {
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [saving, setSaving] = useState(false);
  const [isParsing, setIsParsing] = useState(false);

  const disabled = sketcherValue == null || sketcherValue === initialChemicalStructure || saving || isParsing;

  async function updateMoleculeChemicalStructure(): Promise<void> {
    setSaving(true);

    try {
      await withLoadingScreen(
        MoleculeActions.updateChemicalStructure(
          moleculeId,
          expectedCurrentVersionId,
          ChemicalFormatType.MOLFILE,
          sketcherValue!
        ),
        'Updating molecule...'
      );
      onRequestSubmit();
    } catch (err) {
      setSaving(false);
      throw err;
    }
  }

  return (
    <ExpandableModal
      onRequestClose={onRequestClose}
      onRequestSubmit={updateMoleculeChemicalStructure}
      startMaximized={true}
      title="Edit chemical structure"
      buttons={[{disabled, submit: true, text: 'Save'}]}
    >
      {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
        <ChemicalCounterpartFields sketcherValue={sketcherValue} setIsParsing={setIsParsing} />
      )}
      <ChemicalStructureModalInput
        initialSketcherValue={initialChemicalStructure}
        onSketcherValueChange={setSketcherValue}
        mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
        inputFormat={null}
        outputFormat={MarvinChemicalFormat.MOLV3}
      />
    </ExpandableModal>
  );
}
